import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Key, Car, Train, Phone, MapPin, Info } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import type { ArrivalDepartureInfo } from '../../types';

interface ArrivalDepartureDisplayProps {
  info?: ArrivalDepartureInfo;
}

const ArrivalDepartureDisplay: React.FC<ArrivalDepartureDisplayProps> = ({ info }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  if (!info) {
    return (
      <div className="text-muted-foreground text-center py-8">
        {t('common.noContent')}
      </div>
    );
  }

  const InfoRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string;
  }> = ({ icon, label, value }) => (
    <div className="flex gap-3 py-3 border-b border-border last:border-0">
      <div className="text-muted-foreground flex-shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1">
        <div className="text-sm font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 whitespace-pre-line">{value}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Check-in / Check-out Times */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <Clock className="h-6 w-6 text-green-600" />
              <div>
                <div className="text-sm font-medium text-green-700">{t('arrival.checkIn')}</div>
                <div className="text-xl font-bold text-green-800">{info.checkInTime}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Clock className="h-6 w-6 text-orange-600" />
              <div>
                <div className="text-sm font-medium text-orange-700">{t('arrival.checkOut')}</div>
                <div className="text-xl font-bold text-orange-800">{info.checkOutTime}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details */}
      <Card>
        <CardContent className="pt-6">
          <InfoRow
            icon={<Key className="h-5 w-5" />}
            label={t('arrival.keyInstructions')}
            value={info.keyInstructions[language]}
          />
          <InfoRow
            icon={<MapPin className="h-5 w-5" />}
            label={t('arrival.parking')}
            value={info.parkingInfo[language]}
          />
          <InfoRow
            icon={<Car className="h-5 w-5" />}
            label={t('arrival.directionsByCar')}
            value={info.directionsByCar[language]}
          />
          <InfoRow
            icon={<Train className="h-5 w-5" />}
            label={t('arrival.directionsByTrain')}
            value={info.directionsByTrain[language]}
          />
          <InfoRow
            icon={<Phone className="h-5 w-5" />}
            label={t('arrival.contact')}
            value={info.contactNumber}
          />
          {info.additionalNotes && info.additionalNotes[language] && (
            <InfoRow
              icon={<Info className="h-5 w-5" />}
              label={t('arrival.additionalNotes')}
              value={info.additionalNotes[language]}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ArrivalDepartureDisplay;
